import React from "react";
import {
  CUT_METHODS,
  VLT_PRESETS,
  TINT_WINDOWS,
  PPF_FINISHES,
  PPF_COVERAGE,
  emptySpec,
} from "../lib/jobTypes";

/**
 * Dynamic extra-fields block for Tinting / PPF jobs.
 * Renders nothing for other job types.
 *
 * Props:
 *   jobType : string          "tinting" | "ppf" | anything else
 *   spec    : object          current form.spec value
 *   onChange: (spec) => void  updates parent form.spec
 *   idPrefix: string          data-testid namespace (e.g. "job" or "convert")
 */
export default function JobSpecFields({ jobType, spec, onChange, idPrefix = "job" }) {
  if (jobType !== "tinting" && jobType !== "ppf") return null;

  const s = spec && Object.keys(spec).length ? spec : emptySpec(jobType);

  const setField = (key, value) => onChange({ ...s, [key]: value });

  const toggleInArray = (key, value) => {
    const current = Array.isArray(s[key]) ? s[key] : [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...s, [key]: next });
  };

  const isTint = jobType === "tinting";

  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-4"
      data-testid={`${idPrefix}-spec-block`}
    >
      <div className="flex items-center justify-between">
        <div className="text-label">
          {isTint ? "Tinting details" : "PPF details"}
        </div>
        <span className="pill pill-muted text-[10px]">
          {isTint ? "TINT" : "PPF"}
        </span>
      </div>

      {/* Cut method (shared) */}
      <div>
        <label className="text-label block mb-1.5">Cut method</label>
        <div className="grid grid-cols-2 gap-2">
          {CUT_METHODS.map((c) => (
            <button
              key={c.value}
              type="button"
              data-testid={`${idPrefix}-spec-cut-${c.value}`}
              onClick={() => setField("cut_method", c.value)}
              className={`text-xs rounded-2xl border py-2 px-2 transition ${
                s.cut_method === c.value
                  ? "bg-white text-black border-white font-semibold"
                  : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {isTint && (
        <>
          {/* VLT presets */}
          <div>
            <label className="text-label block mb-1.5">VLT (Visible Light Transmission)</label>
            <div className="flex flex-wrap gap-2">
              {VLT_PRESETS.map((v) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`${idPrefix}-spec-vlt-${v.replace("%", "")}`}
                  onClick={() => setField("vlt", v)}
                  className={`text-xs rounded-full border px-3 py-1.5 transition ${
                    s.vlt === v
                      ? "bg-white text-black border-white font-semibold"
                      : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Windows */}
          <div>
            <label className="text-label block mb-1.5">Windows to tint</label>
            <div className="grid grid-cols-2 gap-2">
              {TINT_WINDOWS.map((w) => {
                const active = (s.windows || []).includes(w.value);
                return (
                  <button
                    key={w.value}
                    type="button"
                    data-testid={`${idPrefix}-spec-window-${w.value}`}
                    onClick={() => toggleInArray("windows", w.value)}
                    className={`text-xs rounded-2xl border py-2 transition ${
                      active
                        ? "bg-white text-black border-white font-semibold"
                        : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
                    }`}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {!isTint && (
        <>
          {/* PPF finish */}
          <div>
            <label className="text-label block mb-1.5">Finish</label>
            <div className="grid grid-cols-2 gap-2">
              {PPF_FINISHES.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  data-testid={`${idPrefix}-spec-finish-${f.value}`}
                  onClick={() => setField("finish", f.value)}
                  className={`text-xs rounded-2xl border py-2 transition ${
                    s.finish === f.value
                      ? "bg-white text-black border-white font-semibold"
                      : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Coverage areas */}
          <div>
            <label className="text-label block mb-1.5">Coverage areas</label>
            <div className="grid grid-cols-2 gap-2">
              {PPF_COVERAGE.map((c) => {
                const active = (s.coverage || []).includes(c.value);
                return (
                  <button
                    key={c.value}
                    type="button"
                    data-testid={`${idPrefix}-spec-coverage-${c.value}`}
                    onClick={() => toggleInArray("coverage", c.value)}
                    className={`text-xs rounded-2xl border py-2 transition ${
                      active
                        ? "bg-white text-black border-white font-semibold"
                        : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Compact read-only badge string for the job card.
 * Example: "PPF · Full front · Clear · Plot cut"
 */
export const summarizeSpec = (jobType, spec) => {
  if (!spec || typeof spec !== "object") return "";
  const parts = [];
  const cut = spec.cut_method === "bulk_fit" ? "Bulk fit" : spec.cut_method === "plot_cut" ? "Plot cut" : null;

  if (jobType === "tinting") {
    if (spec.vlt) parts.push(spec.vlt);
    if (Array.isArray(spec.windows) && spec.windows.length) {
      parts.push(`${spec.windows.length} window${spec.windows.length > 1 ? "s" : ""}`);
    }
    if (cut) parts.push(cut);
  } else if (jobType === "ppf") {
    if (spec.finish) parts.push(spec.finish === "color" ? "Color PPF" : "Clear PPF");
    if (Array.isArray(spec.coverage) && spec.coverage.length) {
      const map = { full_front: "Full front", partial_front: "Partial front", full_body: "Full body", custom: "Custom" };
      parts.push(spec.coverage.map((c) => map[c] || c).join(" + "));
    }
    if (cut) parts.push(cut);
  }
  return parts.join(" · ");
};
